import type { BillingPortalBodySchema, BillingSubscriptionSummarySchema, BillingUsagePayloadSchema } from '@/generated/api-client'
import { getBillingSubscription, getBillingUsage, postBillingPortal } from '@/generated/api-client'
import { configureOpenApiClient } from '@/lib/api/client/configure-openapi-client'

configureOpenApiClient()

export type BillingSubscriptionSummary = Omit<BillingSubscriptionSummarySchema, 'plan'> & {
    plan: BillingSubscriptionSummarySchema['plan'] & {
        includedCredits?: number | null
        creditCarryoverEnabled?: boolean
    }
}
/** Usage payload; resumeCapacity includes manualRaiseUnits / block charge fields from the API. */
export type BillingUsagePayload = BillingUsagePayloadSchema & {
    resumeCapacity?: BillingUsagePayloadSchema['resumeCapacity'] & {
        manualRaiseUnits?: number
        estimatedBlockChargeUnits?: number
        estimatedBlockChargeCents?: number
    }
}
export type BillingPortalBody = BillingPortalBodySchema

export type BillingPublicPlan = {
    id: number
    code: string
    name: string
    isFreemium: boolean
    amountCents: number | null
    includedCredits?: number | null
    annualDiscountPercent?: number
    annualAmountCents?: number | null
    currency: string
    interval: string
    trialPeriodDays: number
    isCurrent: boolean
    isUpgrade: boolean
    isDowngrade: boolean
}

export async function fetchBillingSubscription(companySlug: string): Promise<BillingSubscriptionSummary | null> {
    const { data } = await getBillingSubscription({
        query: { companySlug },
        throwOnError: true,
    })
    return (data?.data as BillingSubscriptionSummary | null | undefined) ?? null
}

export async function fetchBillingUsage(companySlug: string): Promise<BillingUsagePayload | null> {
    const { data } = await getBillingUsage({
        query: { companySlug },
        throwOnError: true,
    })
    return (data?.data as BillingUsagePayload | null | undefined) ?? null
}

export async function createBillingPortalSession(body: BillingPortalBody): Promise<{ url: string }> {
    const { data } = await postBillingPortal({
        body,
        throwOnError: true,
    })
    const payload = data?.data
    if (!payload?.url) {
        throw new Error('Invalid billing portal response')
    }
    return payload
}

export async function requestBillingCreditPack(input: {
    companySlug: string
    packSize: number
}): Promise<{ checkoutUrl: string; packSize: number; amountCents: number }> {
    const res = await fetch('/api/billing/credit-packs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok) {
        throw new Error((json as { message?: string })?.message ?? 'Failed to start credit pack checkout')
    }
    return (json as { data: { checkoutUrl: string; packSize: number; amountCents: number } }).data
}

export async function fetchBillingPlans(companySlug: string): Promise<BillingPublicPlan[]> {
    const res = await fetch(`/api/billing/plans?companySlug=${encodeURIComponent(companySlug)}`)
    const json = await res.json().catch(() => ({}))
    if (!res.ok) {
        throw new Error((json as { message?: string })?.message ?? 'Failed to load plans')
    }
    return ((json as { data?: BillingPublicPlan[] }).data ?? []) as BillingPublicPlan[]
}

export async function changeBillingPlan(input: {
    companySlug: string
    planId: number
    interval?: 'month' | 'year'
}): Promise<{ checkoutUrl: string | null; updated: boolean; planId?: number }> {
    const res = await fetch('/api/billing/change-plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok) {
        throw new Error((json as { message?: string })?.message ?? 'Failed to change plan')
    }
    return (json as { data: { checkoutUrl: string | null; updated: boolean; planId?: number } }).data
}

/** Reconcile upgrade / credit-pack Checkout when webhooks have not run yet. */
export async function completeBillingCheckout(input: {
    companySlug: string
    sessionId: string
}): Promise<{ purpose: 'upgrade' | 'credit_pack'; applied: boolean; planId?: number | null; packSize?: number | null }> {
    const res = await fetch('/api/billing/complete-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok) {
        throw new Error((json as { message?: string })?.message ?? 'Failed to complete checkout')
    }
    return (
        json as {
            data: {
                purpose: 'upgrade' | 'credit_pack'
                applied: boolean
                planId?: number | null
                packSize?: number | null
            }
        }
    ).data
}
